import { reactive, computed } from 'vue'
import { whatsappService } from '@/services/whatsappService'
import { companiesService } from '@/services/CompaniesService'

/**
 * Disponibilidad del WhatsApp de la plataforma + cupo del mes, compartido entre
 * componentes y cacheado por empresa activa.
 *
 * Decide qué recordatorio se ofrece en una cita:
 *   - `available === false` (plan free, toggle apagado o error) -> enlace wa.me gratuito
 *   - `available === true`  (basic+)                             -> envío por la API
 * Ante cualquier error se queda en `false`: es el comportamiento seguro y gratuito.
 */

const CACHE_TTL_MS = 60 * 1000

const state = reactive({
  data: null,
  loading: false,
  companyKey: null,
  loadedAt: 0
})

let inflight = null

const currentCompanyKey = () => String(companiesService.getActiveCompany()?.id ?? '')

async function load({ force = false } = {}) {
  const key = currentCompanyKey()
  const fresh = state.companyKey === key && Date.now() - state.loadedAt < CACHE_TTL_MS

  if (!force && fresh && state.data) return state.data
  if (!force && inflight && state.companyKey === key) return inflight

  state.loading = true
  state.companyKey = key

  inflight = whatsappService
    .getUsage()
    .then((res) => {
      // Si mientras tanto cambió la empresa activa, se descarta la respuesta vieja.
      if (state.companyKey !== key) return state.data
      state.data = res?.data ?? null
      state.loadedAt = Date.now()
      return state.data
    })
    .catch(() => {
      if (state.companyKey === key) {
        state.data = null
        state.loadedAt = Date.now()
      }
      return null
    })
    .finally(() => {
      state.loading = false
      inflight = null
    })

  return inflight
}

/** Solo para pruebas: el estado es de módulo y se compartiría entre specs. */
export function resetWhatsAppUsageCache() {
  state.data = null
  state.loading = false
  state.companyKey = null
  state.loadedAt = 0
  inflight = null
}

export function useWhatsAppUsage() {
  const available = computed(
    () => state.companyKey === currentCompanyKey() && state.data?.api_available === true
  )
  const usage = computed(() => state.data?.usage ?? null)
  const manualResend = computed(() => state.data?.manual_resend ?? null)
  const dryRun = computed(() => state.data?.dry_run === true)

  return {
    available,
    usage,
    manualResend,
    dryRun,
    loading: computed(() => state.loading),
    load
  }
}
